import { apiClient } from '@/api/generated/client'
import type { AccountPick, AccountSearchInput } from '@/api/generated/types'

/**
 * Запись кабинета на сервер — сразу за записью в браузер.
 *
 * Модули хранилища (searchHistory, leadLedger, specStorage) по-прежнему пишут localStorage: его
 * синхронно читают конструкторы сторов мастера и кабинета, и переписывать их на ожидание сети значило
 * бы переписать половину приложения. localStorage теперь — копия, а источник — сервер: каждая запись
 * уходит туда же, а при входе копия заполняется с сервера (accountSync.ts).
 *
 * До входа не пишется ничего: аноним на сервере кабинета не имеет, а его данные уедут при первом
 * входе одним переносом. Ошибка записи проглатывается — копия в браузере цела, следующая запись или
 * следующий вход дошлют своё.
 */

let signedIn = false

export const setAccountSignedIn = (value: boolean) => {
  signedIn = value
}

export const isAccountSignedIn = () => signedIn

const quietly = (promise: Promise<unknown>) => {
  void promise.catch(() => {})
}

/* Анкета и мастер пишутся на каждое нажатие — их сервер получает с задержкой, последним значением. */
const DEBOUNCE_MS = 800
const timers = new Map<string, ReturnType<typeof setTimeout>>()
const pending = new Map<string, () => Promise<unknown>>()

const later = (key: string, send: () => Promise<unknown>) => {
  const timer = timers.get(key)
  if (timer) clearTimeout(timer)
  pending.set(key, send)
  timers.set(
    key,
    setTimeout(() => {
      timers.delete(key)
      const job = pending.get(key)
      pending.delete(key)
      if (job) quietly(job())
    }, DEBOUNCE_MS),
  )
}

/** Дослать отложенное сейчас — перед уходом со страницы и перед выходом из аккаунта. */
export const flushAccountPush = async () => {
  const jobs = [...pending.values()]
  for (const timer of timers.values()) clearTimeout(timer)
  timers.clear()
  pending.clear()
  await Promise.allSettled(jobs.map((job) => job()))
}

if (typeof window !== 'undefined') {
  /* Тесты на Node подменяют `window` пустым объектом (scripts/tsResolve.mjs): модуль, который на загрузке
   вешает слушатель, ронял там каждый тест, импортирующий searchHistory (shareView.test после слияния
   с main 2026-10-07). В браузере слушатель есть всегда. */
if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
  window.addEventListener('pagehide', () => void flushAccountPush())
}
}

/** Значение с отметкой времени: при входе сервер и браузер сравнивают, чья запись свежее. */
export type Stamped<T> = { at: string; value: T }

export const pushSpec = (stamped: Stamped<unknown>) => {
  if (signedIn) later('spec', () => apiClient.me.saveState({ spec: stamped as Record<string, unknown> }))
}

export const pushWizard = (stamped: Stamped<unknown>) => {
  if (signedIn) later('wizard', () => apiClient.me.saveState({ wizard: stamped as Record<string, unknown> }))
}

export const pushCompanies = (stamped: Stamped<unknown[]>) => {
  if (signedIn) later('companies', () => apiClient.me.saveState({ companies: stamped as unknown as Record<string, unknown> }))
}

export const pushCampaigns = (stamped: Stamped<unknown[]>) => {
  if (signedIn) later('campaigns', () => apiClient.me.saveState({ campaigns: stamped as unknown as Record<string, unknown> }))
}

/** Имя и роль из профиля кабинета. */
export const pushProfile = (profile: { name: string; role: string }) => {
  if (signedIn) quietly(apiClient.me.saveState({ profile }))
}

export const pushPlan = (plan: Record<string, unknown> | null) => {
  if (signedIn) quietly(apiClient.me.saveState({ plan }))
}

export const pushSearch = (search: AccountSearchInput) => {
  if (signedIn) quietly(apiClient.me.saveSearch({ search }))
}

export const pushPicks = (picks: Record<string, AccountPick[]>) => {
  if (signedIn) later('picks', () => apiClient.me.savePicks({ picks }))
}

export const pushLedger = (seen: string[]) => {
  if (signedIn && seen.length) quietly(apiClient.me.saveLedger({ seen }))
}

